import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  DragStartEvent,
  DragEndEvent,
  pointerWithin,
} from '@dnd-kit/core';
import { onGameEvent, emitGameEvent } from '../../core/GameEvents';
import { networkManager } from '../../systems/NetworkManager';
import type { InventorySlotData, ShipInventoryItem, InventoryData } from '../../systems/NetworkManager';
import { getEquipmentById } from '@astra/shared';
import { SHIPS } from '@astra/shared';
import { RESOURCES, DRONE_CONFIG } from '@astra/shared';

const SALVAGE_LABELS: Record<string, string> = {
  miner: 'Miner Drone Salvage',
  hauler: 'Hauler Drone Salvage',
  fighter: 'Fighter Drone Salvage',
};

function getSalvageLabel(resourceId: string): string | null {
  if (!resourceId.startsWith(`${DRONE_CONFIG.SALVAGE_ITEM_PREFIX}_`)) return null;
  const role = resourceId.slice(DRONE_CONFIG.SALVAGE_ITEM_PREFIX.length + 1);
  return SALVAGE_LABELS[role] ?? `${role[0]?.toUpperCase() ?? ''}${role.slice(1)} Drone Salvage`;
}

const PALETTE = {
  DARK: '#0f1b26',
  DARK_TRANSPARENT: 'rgba(15, 27, 38, 0.85)',
  LIGHT: '#f5e8d1',
  TEAL: '#20a5a6',
  ORANGE: '#dd5639',
  DIM: '#4a5a6a',
  PANEL: '#1a2a38',
  PANEL_TRANSPARENT: 'rgba(26, 42, 56, 0.9)',
  GREEN: '#4ade80',
  RED: '#ef4444',
  YELLOW: '#fbbf24',
  PURPLE: '#a78bfa',
  BLUE: '#38bdf8',
};

// Slot type colors
const SLOT_COLORS: Record<string, string> = {
  ENGINE: '#fbbf24',
  WEAPON: '#ef4444',
  TOOL: '#4ade80',
  SHIELD: '#38bdf8',
  ENHANCER: '#60d4a0',
  UTILITY: '#a78bfa',
  DRONE: '#f97316',
};

// Resource colors
const RESOURCE_COLORS: Record<string, string> = {
  iron: '#a1a1aa',
  copper: '#f97316',
  titanium: '#38bdf8',
  platinum: '#a78bfa',
  water_ice: '#67e8f9',
  ammonia: '#86efac',
  methane: '#fbbf24',
  helium3: '#f0abfc',
  hydrogen: '#fca5a5',
  nitrogen: '#93c5fd',
  xenon: '#c4b5fd',
  antimatter: '#f472b6',
  solar_charge: '#fcd34d',
  plasma_core: '#fb923c',
  fusion_matter: '#60a5fa',
  exotic_matter: '#e879f9',
};

interface ShipInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Draggable item component
function DraggableItem({
  id,
  children,
  data,
  disabled,
}: {
  id: string;
  children: React.ReactNode;
  data: { type: 'equipped' | 'inventory'; slotIndex?: number; equipmentId?: string };
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'grab',
        touchAction: 'none',
      }}
    >
      {children}
    </div>
  );
}

// Droppable slot component
function DroppableSlot({
  id,
  children,
  data,
  isOver,
  canDrop,
}: {
  id: string;
  children: React.ReactNode;
  data: { slotType: string; slotIndex: number; size: string };
  isOver?: boolean;
  canDrop?: boolean;
}) {
  const { setNodeRef, isOver: dropping } = useDroppable({
    id,
    data,
  });

  const showDropIndicator = dropping && canDrop;

  return (
    <div
      ref={setNodeRef}
      style={{
        border: showDropIndicator ? `2px solid ${PALETTE.GREEN}` : undefined,
        backgroundColor: showDropIndicator ? `${PALETTE.GREEN}22` : undefined,
        borderRadius: '4px',
        transition: 'all 0.15s ease',
      }}
    >
      {children}
    </div>
  );
}

// Discard modal for quantity selection and confirmation
interface DiscardModalState {
  itemId: string;
  itemName: string;
  maxQuantity: number;
  isResource: boolean;
}

function DiscardModal({
  state,
  onConfirm,
  onCancel,
}: {
  state: DiscardModalState;
  onConfirm: (quantity: number) => void;
  onCancel: () => void;
}) {
  const [quantity, setQuantity] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleQuantityChange = (value: number) => {
    setQuantity(Math.max(1, Math.min(value, state.maxQuantity)));
  };

  const handleContinue = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    onConfirm(quantity);
  };

  if (showConfirmation) {
    return (
      <div style={discardModalOverlayStyle}>
        <div style={discardModalStyle}>
          <div style={discardModalTitleStyle}>Confirm Discard</div>
          <div style={discardModalContentStyle}>
            <p style={discardModalTextStyle}>
              Are you sure you want to discard{' '}
              <span style={discardHighlightStyle}>
                {quantity}x {state.itemName}
              </span>
              ?
            </p>
            <p style={discardWarningStyle}>This action cannot be undone.</p>
          </div>
          <div style={discardModalButtonsStyle}>
            <button style={discardCancelButtonStyle} onClick={onCancel}>
              Cancel
            </button>
            <button style={discardConfirmButtonStyle} onClick={handleConfirm}>
              Discard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={discardModalOverlayStyle}>
      <div style={discardModalStyle}>
        <div style={discardModalTitleStyle}>Discard {state.itemName}</div>
        <div style={discardModalContentStyle}>
          <p style={discardModalTextStyle}>Select quantity to discard:</p>
          <div style={quantitySelectorStyle}>
            <button
              style={quantityButtonStyle}
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
            >
              -
            </button>
            <input
              type="number"
              style={quantityInputStyle}
              value={quantity}
              onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
              min={1}
              max={state.maxQuantity}
            />
            <button
              style={quantityButtonStyle}
              onClick={() => handleQuantityChange(quantity + 1)}
              disabled={quantity >= state.maxQuantity}
            >
              +
            </button>
          </div>
          <div style={quantityPresetsStyle}>
            <button style={presetButtonStyle} onClick={() => setQuantity(1)}>
              1
            </button>
            {state.maxQuantity >= 10 && (
              <button style={presetButtonStyle} onClick={() => setQuantity(10)}>
                10
              </button>
            )}
            {state.maxQuantity >= 50 && (
              <button style={presetButtonStyle} onClick={() => setQuantity(50)}>
                50
              </button>
            )}
            <button style={presetButtonStyle} onClick={() => setQuantity(state.maxQuantity)}>
              All ({state.maxQuantity})
            </button>
          </div>
        </div>
        <div style={discardModalButtonsStyle}>
          <button style={discardCancelButtonStyle} onClick={onCancel}>
            Cancel
          </button>
          <button style={discardContinueButtonStyle} onClick={handleContinue}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export function ShipInventoryModal({ isOpen, onClose }: ShipInventoryModalProps) {
  const [slots, setSlots] = useState<InventorySlotData[]>([]);
  const [shipInventory, setShipInventory] = useState<ShipInventoryItem[]>([]);
  const [shipClassId, setShipClassId] = useState('corvette_t1');
  const [shipName, setShipName] = useState('');
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeData, setActiveData] = useState<any>(null);
  const [discardModal, setDiscardModal] = useState<DiscardModalState | null>(null);
  const [visible, setVisible] = useState(false);

  // Animate drawer in/out
  useEffect(() => {
    if (isOpen) {
      // Trigger slide-in on next frame
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  // Calculate cargo capacity
  const shipClass = SHIPS[shipClassId];
  const baseCargoCapacity = shipClass?.stats.cargoCapacity || 10;

  // Calculate bonus from cargo expanders
  let cargoBonus = 0;
  for (const slot of slots) {
    if (slot.equipped) {
      const equipment = getEquipmentById(slot.equipped);
      if (equipment && 'utilityType' in equipment && (equipment as any).utilityType === 'cargo_expander') {
        cargoBonus += (equipment as any).stats?.cargoBonus || 0;
      }
    }
  }
  const cargoCapacity = baseCargoCapacity + cargoBonus;

  // Calculate cargo used
  let cargoUsed = 0;
  for (const item of shipInventory) {
    if (item.itemType === 'resource') {
      cargoUsed += item.quantity;
    } else if (item.itemType === 'equipment') {
      const eq = getEquipmentById(item.itemId);
      cargoUsed += (eq?.volume || 1) * item.quantity;
    }
  }

  // Load inventory data
  const loadInventory = useCallback(() => {
    setLoading(true);
    networkManager.requestInventoryData('');
  }, []);

  // Handle inventory data from server
  useEffect(() => {
    const handleInventoryData = (data: InventoryData) => {
      setSlots(data.slots || []);
      setShipInventory(data.shipInventory || []);
      setShipClassId(data.shipClassId);
      setShipName(data.shipName);
      setCredits(data.credits);
      setLoading(false);
    };

    networkManager.onInventoryData = handleInventoryData;

    networkManager.onEquipResult = (data) => {
      if (data.success) {
        loadInventory();
        emitGameEvent('notification', { type: 'success', message: 'Item equipped' });
      } else {
        setLoading(false);
        emitGameEvent('notification', { type: 'error', message: data.error || 'Failed to equip' });
      }
    };

    networkManager.onUnequipResult = (data) => {
      if (data.success) {
        loadInventory();
        emitGameEvent('notification', { type: 'success', message: 'Item unequipped' });
      } else {
        setLoading(false);
        emitGameEvent('notification', { type: 'error', message: data.error || 'Failed to unequip' });
      }
    };

    networkManager.onDiscardResult = (data) => {
      if (data.success) {
        loadInventory();
        emitGameEvent('notification', { type: 'info', message: 'Item discarded' });
      } else {
        setLoading(false);
        emitGameEvent('notification', { type: 'error', message: data.error || 'Failed to discard' });
      }
    };

    return () => {
      networkManager.onInventoryData = null;
      networkManager.onEquipResult = null;
      networkManager.onUnequipResult = null;
      networkManager.onDiscardResult = null;
    };
  }, [loadInventory]);

  // Load data when opened
  useEffect(() => {
    if (isOpen) {
      loadInventory();
    }
  }, [isOpen, loadInventory]);

  // Re-fetch when the server reports a cargo change (mining tick, loot, etc.)
  // so an open panel doesn't show stale data. Debounced 200ms to coalesce
  // bursts (e.g. several mining ticks landing inside one frame).
  useEffect(() => {
    if (!isOpen) return;
    let timer: number | null = null;
    const unsub = onGameEvent('cargoChanged', () => {
      if (timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        loadInventory();
      }, 200);
    });
    return () => {
      unsub();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [isOpen, loadInventory]);

  // Subscribe to credits updates
  useEffect(() => {
    const unsub = onGameEvent('playerStateUpdate', (data) => {
      setCredits(data.credits);
    });
    return unsub;
  }, []);

  // Keyboard handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setActiveData(event.active.data.current);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveData(null);

    if (!over || loading) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Dragging from inventory to equipment slot
    if (activeData?.type === 'inventory' && overData?.slotType) {
      const equipmentId = activeData.equipmentId;
      const equipment = getEquipmentById(equipmentId);

      if (equipment && equipment.slotType === overData.slotType) {
        // Check size compatibility
        const sizeOrder = ['SMALL', 'MEDIUM', 'LARGE'];
        const eqSizeIdx = sizeOrder.indexOf(equipment.size);
        const slotSizeIdx = sizeOrder.indexOf(overData.size);

        if (eqSizeIdx <= slotSizeIdx) {
          setLoading(true);
          networkManager.sendEquipItem('', equipmentId, overData.slotIndex);
        } else {
          emitGameEvent('notification', { type: 'warning', message: 'Equipment too large for this slot' });
        }
      } else {
        emitGameEvent('notification', { type: 'warning', message: 'Incompatible slot type' });
      }
    }

    // Dragging from equipment slot to inventory area
    if (activeData?.type === 'equipped' && over.id === 'inventory-drop-zone') {
      setLoading(true);
      networkManager.sendUnequipItem('', activeData.slotIndex);
    }
  };

  // Check if equipment can be dropped on a slot
  const canDropOnSlot = (equipmentId: string, slotType: string, slotSize: string): boolean => {
    const equipment = getEquipmentById(equipmentId);
    if (!equipment) return false;
    if (equipment.slotType !== slotType) return false;

    const sizeOrder = ['SMALL', 'MEDIUM', 'LARGE'];
    const eqSizeIdx = sizeOrder.indexOf(equipment.size);
    const slotSizeIdx = sizeOrder.indexOf(slotSize);
    return eqSizeIdx <= slotSizeIdx;
  };

  const handleDiscard = (itemId: string, quantity: number = 1) => {
    if (loading) return;
    setLoading(true);
    networkManager.sendDiscardItem(itemId, quantity);
  };

  // Open discard modal for items with quantity > 1
  const openDiscardModal = (itemId: string, itemName: string, maxQuantity: number, isResource: boolean) => {
    if (maxQuantity <= 1) {
      // For single items, skip to confirmation
      setDiscardModal({ itemId, itemName, maxQuantity: 1, isResource });
    } else {
      setDiscardModal({ itemId, itemName, maxQuantity, isResource });
    }
  };

  const handleDiscardConfirm = (quantity: number) => {
    if (discardModal) {
      handleDiscard(discardModal.itemId, quantity);
      setDiscardModal(null);
    }
  };

  const handleDiscardCancel = () => {
    setDiscardModal(null);
  };

  if (!isOpen) return null;

  // Group slots by type
  const slotsByType: Record<string, InventorySlotData[]> = {};
  for (const slot of slots) {
    if (!slotsByType[slot.slotType]) {
      slotsByType[slot.slotType] = [];
    }
    slotsByType[slot.slotType].push(slot);
  }

  // Calculate AIP (Average Item Power) — sum of equipped items' power cost
  let totalPower = 0;
  for (const slot of slots) {
    if (slot.equipped) {
      const eq = getEquipmentById(slot.equipped);
      if (eq) totalPower += eq.power;
    }
  }

  // Separate inventory items
  const cargoEquipment = shipInventory.filter(i => i.itemType === 'equipment');
  const cargoResources = shipInventory.filter(i => i.itemType === 'resource');

  // Check if equipment can fit in ANY slot on the current ship
  const canFitInAnySlot = (equipmentId: string): boolean => {
    const equipment = getEquipmentById(equipmentId);
    if (!equipment) return false;
    return slots.some(slot => canDropOnSlot(equipmentId, slot.slotType, slot.size));
  };

  // Get dragged item info for overlay
  const getDraggedItemInfo = () => {
    if (!activeData) return null;

    if (activeData.type === 'equipped') {
      const slot = slots.find(s => s.slotIndex === activeData.slotIndex);
      if (slot?.equipped) {
        return getEquipmentById(slot.equipped);
      }
    } else if (activeData.type === 'inventory') {
      return getEquipmentById(activeData.equipmentId);
    }
    return null;
  };

  const draggedItem = getDraggedItemInfo();

  return (
    <div style={drawerOverlayStyle(visible)} onClick={onClose}>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        collisionDetection={pointerWithin}
      >
        <div style={drawerStyle(visible)} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={headerStyle}>
            <div style={headerLeftStyle}>
              <span style={titleStyle}>{shipName || shipClass?.name || 'SHIP'}</span>
              <span style={powerStyle}>AIP: {totalPower}</span>
            </div>
            <div style={headerRightStyle}>
              <span style={creditsStyle}>CREDITS: {credits.toLocaleString()}</span>
              <button style={closeButtonStyle} onClick={onClose}>×</button>
            </div>
          </div>

          {/* Loading overlay */}
          {loading && (
            <div style={loadingOverlayStyle}>
              <div style={loadingSpinnerStyle}>
                <span style={loadingTextStyle}>Processing...</span>
              </div>
            </div>
          )}

          {/* Two-pane content */}
          <div style={contentStyle}>
            {/* Left pane: Equipment Slots */}
            <div style={leftPaneStyle}>
              <div style={paneTitleStyle}>EQUIPMENT SLOTS</div>
              <div style={slotsContainerStyle}>
                {Object.entries(slotsByType).map(([slotType, typeSlots]) => (
                  <div key={slotType} style={slotSectionStyle}>
                    <div style={slotLabelStyle(SLOT_COLORS[slotType] || PALETTE.DIM)}>
                      {slotType}
                    </div>
                    {typeSlots.map((slot) => {
                      const equipment = slot.equipped ? getEquipmentById(slot.equipped) : null;
                      const canDrop = activeData?.type === 'inventory' &&
                        canDropOnSlot(activeData.equipmentId, slot.slotType, slot.size);

                      return (
                        <DroppableSlot
                          key={slot.slotIndex}
                          id={`slot-${slot.slotIndex}`}
                          data={{ slotType: slot.slotType, slotIndex: slot.slotIndex, size: slot.size }}
                          canDrop={canDrop}
                        >
                          <div style={slotRowStyle(!!equipment, SLOT_COLORS[slotType], canDrop)}>
                            {equipment ? (
                              <DraggableItem
                                id={`equipped-${slot.slotIndex}`}
                                data={{ type: 'equipped', slotIndex: slot.slotIndex }}
                                disabled={loading}
                              >
                                <div style={equippedItemStyle}>
                                  <div style={equipmentInfoStyle}>
                                    <span style={equipmentNameStyle}>{equipment.name}</span>
                                    <span style={equipmentTierStyle}>T{equipment.tier}</span>
                                  </div>
                                  <span style={dragHintStyle}>drag to unequip</span>
                                </div>
                              </DraggableItem>
                            ) : (
                              <div style={emptySlotStyle}>
                                <span style={activeData?.type === 'inventory'
                                  ? (canDrop ? compatibleTextStyle : notCompatibleTextStyle)
                                  : emptySlotTextStyle
                                }>
                                  {activeData?.type === 'inventory'
                                    ? (canDrop ? 'COMPATIBLE' : 'NOT COMPATIBLE')
                                    : `Empty ${slot.size.toLowerCase()}`
                                  }
                                </span>
                              </div>
                            )}
                          </div>
                        </DroppableSlot>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div style={dividerStyle} />

            {/* Right pane: Inventory */}
            <InventoryDropZone id="inventory-drop-zone">
              <div style={rightPaneStyle}>
                <div style={paneTitleStyle}>
                  SHIP CARGO
                  <span style={cargoIndicatorStyle(cargoUsed, cargoCapacity)}>
                    {cargoUsed}/{cargoCapacity}
                  </span>
                </div>

                {/* Equipment in cargo */}
                {cargoEquipment.length > 0 && (
                  <div style={inventorySectionStyle}>
                    <div style={inventorySectionTitleStyle}>EQUIPMENT</div>
                    <div style={inventoryGridStyle}>
                      {cargoEquipment.map((item) => {
                        const equipment = getEquipmentById(item.itemId);
                        if (!equipment) return null;
                        const color = SLOT_COLORS[equipment.slotType] || PALETTE.DIM;
                        const canFit = canFitInAnySlot(item.itemId);

                        return (
                          <DraggableItem
                            key={item.itemId}
                            id={`inventory-${item.itemId}`}
                            data={{ type: 'inventory', equipmentId: item.itemId }}
                            disabled={loading}
                          >
                            <div style={canFit ? inventoryItemStyle(color) : incompatibleItemStyle(color)}>
                              <div style={inventoryItemInfoStyle}>
                                <span style={canFit ? inventoryItemNameStyle : incompatibleItemNameStyle}>{equipment.name}</span>
                                <span style={inventoryItemTypeStyle(color)}>
                                  {equipment.slotType} T{equipment.tier}
                                </span>
                                <span style={sizeBadgeStyle}>
                                  {equipment.size}
                                </span>
                                <span style={volumeBadgeStyle}>
                                  {(equipment.volume || 1) * item.quantity} vol
                                </span>
                                {item.quantity > 1 && (
                                  <span style={quantityBadgeStyle}>x{item.quantity}</span>
                                )}
                                {!canFit && (
                                  <span style={noFitBadgeStyle}>NO FIT</span>
                                )}
                              </div>
                              <div style={inventoryItemActionsStyle}>
                                <button
                                  style={discardButtonStyle}
                                  onClick={() => openDiscardModal(item.itemId, equipment.name, item.quantity, false)}
                                  disabled={loading}
                                  title="Discard"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          </DraggableItem>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Resources */}
                {cargoResources.length > 0 && (
                  <div style={inventorySectionStyle}>
                    <div style={inventorySectionTitleStyle}>RESOURCES</div>
                    <div style={resourceGridStyle}>
                      {cargoResources.map((item) => {
                        const resourceId = item.itemId.replace('resource_', '');
                        const resourceInfo = RESOURCES[resourceId.toUpperCase() as keyof typeof RESOURCES];
                        const salvageLabel = getSalvageLabel(resourceId);
                        const displayName = salvageLabel ?? resourceInfo?.name ?? resourceId;
                        const color = RESOURCE_COLORS[resourceId] || PALETTE.TEAL;

                        return (
                          <div key={item.itemId} style={resourceItemStyle(color)}>
                            <div style={resourceInfoStyle}>
                              <span style={resourceNameStyle}>
                                {displayName}
                              </span>
                              <span style={volumeBadgeStyle}>{item.quantity} vol</span>
                              <span style={resourceQuantityStyle(color)}>x{item.quantity}</span>
                            </div>
                            <button
                              style={discardResourceButtonStyle}
                              onClick={() => openDiscardModal(item.itemId, displayName, item.quantity, true)}
                              disabled={loading}
                              title="Discard"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {cargoEquipment.length === 0 && cargoResources.length === 0 && (
                  <div style={emptyCargoStyle}>
                    {activeData?.type === 'equipped'
                      ? 'Drop here to unequip'
                      : 'Cargo hold is empty'}
                  </div>
                )}
              </div>
            </InventoryDropZone>
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <span>Drag equipment between slots and inventory</span>
            <span style={footerHintStyle}>Press I to close</span>
          </div>

          {/* Discard confirmation modal */}
          {discardModal && (
            <DiscardModal
              state={discardModal}
              onConfirm={handleDiscardConfirm}
              onCancel={handleDiscardCancel}
            />
          )}
        </div>

        {/* Drag overlay */}
        <DragOverlay>
          {draggedItem && (
            <div style={dragOverlayStyle(SLOT_COLORS[draggedItem.slotType])}>
              <span>{draggedItem.name}</span>
              <span style={dragOverlayTierStyle}>T{draggedItem.tier}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

// Inventory drop zone component
function InventoryDropZone({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        flex: 1,
        backgroundColor: isOver ? `${PALETTE.TEAL}11` : 'transparent',
        borderRadius: '4px',
        transition: 'background-color 0.15s ease',
      }}
    >
      {children}
    </div>
  );
}

// Styles
const drawerOverlayStyle = (visible: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: visible ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0)',
  zIndex: 100,
  pointerEvents: 'auto',
  transition: 'background-color 0.3s ease',
});

const drawerStyle = (visible: boolean): React.CSSProperties => ({
  position: 'absolute',
  top: 0,
  right: 0,
  width: '704px',
  maxWidth: '95vw',
  height: '100%',
  backgroundColor: 'rgba(15, 27, 38, 0.92)',
  borderLeft: `2px solid ${PALETTE.TEAL}`,
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'JetBrains Mono', monospace",
  color: PALETTE.LIGHT,
  boxShadow: `-4px 0 40px ${PALETTE.TEAL}22`,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
  transform: visible ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 0.3s ease',
});

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 24px',
  borderBottom: `1px solid ${PALETTE.DIM}44`,
  backgroundColor: PALETTE.PANEL_TRANSPARENT,
};

const headerLeftStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
};

const headerRightStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
};

const titleStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.TEAL,
  textTransform: 'uppercase',
  letterSpacing: '3px',
};

const powerStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.YELLOW,
  fontWeight: 600,
};

const creditsStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.TEAL,
};

const closeButtonStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: 'none',
  color: PALETTE.DIM,
  fontSize: '22px',
  cursor: 'pointer',
  padding: '0 8px',
  lineHeight: 1,
  transition: 'color 0.2s',
};

const loadingOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 27, 38, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20,
  backdropFilter: 'blur(2px)',
};

const loadingSpinnerStyle: React.CSSProperties = {
  padding: '20px 40px',
  backgroundColor: PALETTE.PANEL,
  borderRadius: '8px',
  border: `1px solid ${PALETTE.TEAL}`,
};

const loadingTextStyle: React.CSSProperties = {
  color: PALETTE.TEAL,
  fontSize: '11px',
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  padding: '16px',
  gap: '16px',
};

const leftPaneStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const rightPaneStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const dividerStyle: React.CSSProperties = {
  height: '1px',
  backgroundColor: PALETTE.DIM,
  opacity: 0.5,
};

const paneTitleStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  paddingBottom: '8px',
  borderBottom: `1px solid ${PALETTE.DIM}44`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
};

const slotsContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const slotSectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const slotLabelStyle = (color: string): React.CSSProperties => ({
  fontSize: '10px',
  color: color,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '2px',
  paddingLeft: '2px',
});

const slotRowStyle = (hasEquipment: boolean, color?: string, canDrop?: boolean): React.CSSProperties => ({
  padding: '2px',
  borderRadius: '4px',
  transition: 'all 0.15s ease',
});

const equippedItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.TEAL}66`,
  borderRadius: '4px',
  cursor: 'grab',
};

const equipmentInfoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const equipmentNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.LIGHT,
  fontWeight: 600,
};

const equipmentTierStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  padding: '2px 6px',
  borderRadius: '3px',
};

const emptySlotStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px',
  backgroundColor: `${PALETTE.PANEL}88`,
  border: `1px dashed ${PALETTE.DIM}66`,
  borderRadius: '4px',
};

const emptySlotTextStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  textTransform: 'uppercase',
};

const compatibleTextStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.GREEN,
  textTransform: 'uppercase',
  fontWeight: 600,
};

const notCompatibleTextStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.RED,
  textTransform: 'uppercase',
  opacity: 0.7,
};

const dragHintStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.DIM,
  fontStyle: 'italic',
  opacity: 0.7,
};

const cargoIndicatorStyle = (used: number, capacity: number): React.CSSProperties => ({
  padding: '3px 10px',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: used >= capacity ? `${PALETTE.RED}33` : `${PALETTE.TEAL}22`,
  color: used >= capacity ? PALETTE.RED : PALETTE.TEAL,
  border: `1px solid ${used >= capacity ? PALETTE.RED : PALETTE.TEAL}44`,
});

const inventorySectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const inventorySectionTitleStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  textTransform: 'uppercase',
  letterSpacing: '1px',
};

const inventoryGridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const inventoryItemStyle = (color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${color}44`,
  borderRadius: '4px',
  cursor: 'grab',
  transition: 'all 0.15s ease',
});

const incompatibleItemStyle = (color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  backgroundColor: `${PALETTE.RED}08`,
  border: `1px solid ${PALETTE.RED}44`,
  borderRadius: '4px',
  cursor: 'not-allowed',
  transition: 'all 0.15s ease',
  opacity: 0.6,
});

const inventoryItemInfoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flex: 1,
};

const inventoryItemNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.LIGHT,
  fontWeight: 600,
};

const incompatibleItemNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  fontWeight: 600,
};

const inventoryItemTypeStyle = (color: string): React.CSSProperties => ({
  fontSize: '10px',
  color: color,
  textTransform: 'uppercase',
});

const sizeBadgeStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: `${PALETTE.DIM}15`,
  border: `1px solid ${PALETTE.DIM}33`,
  padding: '1px 5px',
  borderRadius: '3px',
  letterSpacing: '0.3px',
};

const volumeBadgeStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.TEAL,
  backgroundColor: `${PALETTE.TEAL}15`,
  border: `1px solid ${PALETTE.TEAL}33`,
  padding: '1px 5px',
  borderRadius: '3px',
  letterSpacing: '0.3px',
};

const quantityBadgeStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  padding: '2px 6px',
  borderRadius: '3px',
};

const noFitBadgeStyle: React.CSSProperties = {
  fontSize: '9px',
  color: PALETTE.RED,
  backgroundColor: `${PALETTE.RED}18`,
  border: `1px solid ${PALETTE.RED}44`,
  padding: '2px 5px',
  borderRadius: '3px',
  letterSpacing: '0.5px',
  fontWeight: 600,
};

const inventoryItemActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const discardButtonStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: `1px solid ${PALETTE.RED}44`,
  borderRadius: '4px',
  color: `${PALETTE.RED}88`,
  fontSize: '11px',
  cursor: 'pointer',
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  transition: 'all 0.2s',
};

const resourceGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: '8px',
};

const resourceItemStyle = (color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${color}33`,
  borderRadius: '4px',
});

const resourceInfoStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

const resourceNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.LIGHT,
  textTransform: 'capitalize',
};

const resourceQuantityStyle = (color: string): React.CSSProperties => ({
  fontSize: '11px',
  fontWeight: 600,
  color: color,
});

const discardResourceButtonStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: `1px solid ${PALETTE.RED}33`,
  borderRadius: '3px',
  color: `${PALETTE.RED}66`,
  fontSize: '11px',
  cursor: 'pointer',
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  transition: 'all 0.2s',
};

const emptyCargoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  color: PALETTE.DIM,
  fontSize: '11px',
  border: `2px dashed ${PALETTE.DIM}44`,
  borderRadius: '8px',
  flex: 1,
};

const footerStyle: React.CSSProperties = {
  padding: '12px 24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '11px',
  color: PALETTE.DIM,
  borderTop: `1px solid ${PALETTE.DIM}44`,
  backgroundColor: PALETTE.PANEL_TRANSPARENT,
};

const footerHintStyle: React.CSSProperties = {
  opacity: 0.7,
};

const dragOverlayStyle = (color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 16px',
  backgroundColor: PALETTE.PANEL,
  border: `2px solid ${color}`,
  borderRadius: '6px',
  boxShadow: `0 4px 20px ${color}44`,
  cursor: 'grabbing',
  fontFamily: "'JetBrains Mono', monospace",
  color: PALETTE.LIGHT,
  fontSize: '11px',
  fontWeight: 600,
});

const dragOverlayTierStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  padding: '2px 6px',
  borderRadius: '3px',
};

// Discard modal styles
const discardModalOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 30,
};

const discardModalStyle: React.CSSProperties = {
  backgroundColor: PALETTE.DARK,
  border: `2px solid ${PALETTE.RED}66`,
  borderRadius: '8px',
  padding: '20px',
  minWidth: '320px',
  maxWidth: '400px',
};

const discardModalTitleStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.ORANGE,
  marginBottom: '16px',
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: '1px',
};

const discardModalContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginBottom: '20px',
};

const discardModalTextStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.LIGHT,
  textAlign: 'center',
  margin: 0,
};

const discardHighlightStyle: React.CSSProperties = {
  color: PALETTE.ORANGE,
  fontWeight: 600,
};

const discardWarningStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.RED,
  textAlign: 'center',
  margin: 0,
  opacity: 0.8,
};

const quantitySelectorStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
};

const quantityButtonStyle: React.CSSProperties = {
  width: '36px',
  height: '36px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  color: PALETTE.LIGHT,
  fontSize: '11px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
};

const quantityInputStyle: React.CSSProperties = {
  width: '80px',
  height: '36px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  color: PALETTE.LIGHT,
  fontSize: '11px',
  textAlign: 'center',
  fontFamily: "'JetBrains Mono', monospace",
};

const quantityPresetsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: '8px',
  flexWrap: 'wrap',
};

const presetButtonStyle: React.CSSProperties = {
  padding: '6px 12px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  color: PALETTE.TEAL,
  fontSize: '11px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  transition: 'all 0.2s',
};

const discardModalButtonsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: '12px',
};

const discardCancelButtonStyle: React.CSSProperties = {
  padding: '10px 24px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  color: PALETTE.LIGHT,
  fontSize: '11px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  transition: 'all 0.2s',
};

const discardContinueButtonStyle: React.CSSProperties = {
  padding: '10px 24px',
  backgroundColor: `${PALETTE.ORANGE}22`,
  border: `1px solid ${PALETTE.ORANGE}`,
  borderRadius: '4px',
  color: PALETTE.ORANGE,
  fontSize: '11px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontWeight: 600,
  transition: 'all 0.2s',
};

const discardConfirmButtonStyle: React.CSSProperties = {
  padding: '10px 24px',
  backgroundColor: `${PALETTE.RED}22`,
  border: `1px solid ${PALETTE.RED}`,
  borderRadius: '4px',
  color: PALETTE.RED,
  fontSize: '11px',
  cursor: 'pointer',
  fontFamily: "'JetBrains Mono', monospace",
  fontWeight: 600,
  transition: 'all 0.2s',
};

export default ShipInventoryModal;
